/**
 * cart-drawer-open.js — opens the UpCart drawer on the cart page.
 *
 * Loaded only from sections/template-cart.liquid, which renders only on templates/cart.json,
 * so this never ships to any other page. No template gate needed anywhere.
 *
 * WHY: /cart has no real cart UI because UpCart's drawer is the cart. Two paths still land
 * shoppers there, and without this they hit a blank page:
 *   1. Shopify's checkout header has a "back to cart" icon hardcoded to /cart, and its
 *      destination is NOT customisable (CheckoutBrandingHeaderCartLink exposes contentType +
 *      image only — no href). Worst possible moment: mid-purchase.
 *   2. Cold arrivals — bookmarks, ads, link-in-bio, SMS.
 * Full write-up: docs/empty-cart-fallback-fix.md
 *
 * TIMING — why this is not a one-liner:
 * UpCart's bundle and this file are both `defer`, so they run in document order — UpCart's tag is
 * in <head>, ours is in <body>. So window.upcartSubscribeCartLoaded should already exist when we
 * run. But CART_LOADED is a plain window CustomEvent with no replay: if it fired before we
 * subscribed we would never hear it and the drawer would never open. So we subscribe AND poll,
 * and stop as soon as UpCart confirms the drawer actually opened.
 *
 * The confirm signal matters — without it we cannot tell a successful open from a call that
 * no-opped because the drawer had not finished mounting. If that signal is unavailable we
 * deliberately downgrade to a single attempt, so we can never re-open a drawer the shopper closed.
 *
 * Every UpCart global is feature-checked. They are app-injected and undocumented, so if the app
 * is removed or renames something this file goes quiet instead of throwing.
 *
 * PORTED from sundae-school, where this was verified against bundle upcart-2-404 on 2026-07-23.
 * The UpCart app embed is confirmed installed here (config/settings_data.json), but this store's
 * bundle version has NOT been re-verified — see the verification checklist in the doc.
 */
(function () {
  var POLL_INTERVAL_MS = 200;
  var GIVE_UP_AFTER_MS = 10000;

  var canConfirm = typeof window.upcartSubscribeCartOpened === 'function';
  var done = false;
  var pollTimer = null;
  var startedAt = Date.now();

  function stop() {
    done = true;
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function requestOpen() {
    if (done) return;
    if (typeof window.upcartOpenCart !== 'function') return;

    window.upcartOpenCart();

    // No way to verify it worked, so take the single shot and stand down rather than
    // risk re-opening a drawer the shopper has closed.
    if (!canConfirm) stop();
  }

  // Authoritative stop signal: UpCart telling us the drawer is genuinely open.
  if (canConfirm) {
    window.upcartSubscribeCartOpened(stop);
  }

  // Preferred path — open the moment UpCart says the cart data is ready.
  if (typeof window.upcartSubscribeCartLoaded === 'function') {
    window.upcartSubscribeCartLoaded(requestOpen);
  }

  // Fallback for the already-fired case described above, and for a late-booting bundle.
  pollTimer = setInterval(function () {
    if (done || Date.now() - startedAt > GIVE_UP_AFTER_MS) {
      stop();
      return;
    }
    requestOpen();
  }, POLL_INTERVAL_MS);
})();
